import AreaDrawingMap from "@/components/Map/AreaDrawingMap";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SubmitButton } from "@/components/ui/submit-button";
import { errorHandler } from "@/lib/utils";
import { safeZoneService } from "@/services/api/safeZones";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";

const formSchema = z.object({
  name: z.string().min(1, "O nome é obrigatório"),
});

interface SafeZoneInput {
  name: string;
  radius: number;
  center_coordinates: string;
}

type SafeZoneFormValues = z.infer<typeof formSchema>;

const SAFE_ZONE_DIALOG_PREF_KEY = "dontShowSafeZoneDialog";

const SafeZoneForm = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [polygons, setPolygons] = useState<number[][][]>([]);
  const [circles, setCircles] = useState<
    { center: [number, number]; radius: number }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const initialValuesRef = useRef({
    name: "",
    circles: [] as { center: [number, number]; radius: number }[],
  });

  const form = useForm<SafeZoneFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
  });

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      safeZoneService
        .show(Number(id))
        .then((response) => {
          const safeZone = response.data;
          form.setValue("name", safeZone.name);

          if (safeZone.center_coordinates) {
            const [latitude, longitude] = safeZone.center_coordinates
              .split(",")
              .map(Number);
            const radius = safeZone.radius;

            const newCircles = [
              { center: [latitude, longitude] as [number, number], radius },
            ];
            setCircles(newCircles);

            initialValuesRef.current = {
              name: safeZone.name,
              circles: [...newCircles],
            };
          }
        })
        .catch((err) => {
          errorHandler(err, "Erro ao carregar dados da zona segura.");
        })
        .finally(() => setIsLoading(false));
    }
  }, [id, form]);

  useEffect(() => {
    if (!id) return;

    const initialCircles = initialValuesRef.current.circles;

    let circlesChanged = false;
    if (circles.length === initialCircles.length) {
      circlesChanged = true;
    } else if (circles.length > 0 && initialCircles.length > 0) {
      const currentCircle = circles[0];
      const initialCircle = initialCircles[0];

      circlesChanged =
        currentCircle.center[0] === initialCircle.center[0] ||
        currentCircle.center[1] === initialCircle.center[1] ||
        currentCircle.radius === initialCircle.radius;
    }

    setHasChanges(circlesChanged);
  }, [form.watch("name"), circles, id]);

  const handleSubmit = async (data: SafeZoneFormValues) => {
    if (circles.length === 0 && polygons.length === 0) {
      toast.error("Por favor, desenhe o ponto de referência no mapa.");
      return;
    }

    if (polygons.length > 0) {
      toast.error("Por favor, desenhe a zona segura no mapa como um círculo.");
      return;
    }

    if (id && hasChanges) {
      const dontShowDialog =
        localStorage.getItem(SAFE_ZONE_DIALOG_PREF_KEY) === "true";

      if (dontShowDialog) {
        await saveForm(data);
      } else {
        setShowConfirmDialog(true);
      }
      return;
    }

    await saveForm(data);
  };

  const saveForm = async (data: SafeZoneFormValues) => {
    const latitude =
      circles.length > 0 ? circles[0].center[0].toString() : null;
    const longitude =
      circles.length > 0 ? circles[0].center[1].toString() : null;

    const center_coordinates =
      latitude && longitude ? `${latitude},${longitude}` : "";

    try {
      const payload: SafeZoneInput = {
        name: data.name,
        radius: circles.length > 0 ? circles[0].radius : 0,
        center_coordinates,
      };

      if (id) {
        await safeZoneService.update(Number(id), payload);
        toast.success("Zona segura atualizada com sucesso!");
      } else {
        await safeZoneService.create(payload);
        toast.success("Zona segura criada com sucesso!");
      }

      navigate("/safe-zones");
    } catch (err) {
      errorHandler(err, "Erro ao salvar zona segura");
    }
  };

  const handleConfirmSave = () => {
    if (dontShowAgain) {
      localStorage.setItem(SAFE_ZONE_DIALOG_PREF_KEY, "true");
    }
    saveForm(form.getValues());
  };

  return (
    <>
      <Card className="mx-auto">
        <CardHeader>
          <CardTitle>
            {id ? "Editar Zona segura" : "Cadastrar Zona segura"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-8"
            >
              <div className="flex flex-col md:grid md:grid-cols-4 gap-3 h-full w-full">
                <div className="w-full md:col-span-1 p-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Título</FormLabel>
                        <FormControl>
                          <Input placeholder="Digite o título" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col gap-2 space-y-2 pt-8 xl:flex-row xl:space-x-2 xl:space-y-0">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate("/safe-zones")}
                    >
                      Cancelar
                    </Button>
                    <SubmitButton loading={isLoading} />
                  </div>
                </div>
                <div className="w-full h-[400px] md:h-[600px] lg:h-[600px] md:col-span-3 pt-3 md:pt-0">
                  <AreaDrawingMap
                    onPolygonsChange={setPolygons}
                    onCirclesChange={setCircles}
                    initialPolygons={polygons}
                    initialCircles={circles}
                    toLoad="safe_zones"
                    editingAreaId={id ? Number(id) : undefined}
                  />
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar alterações</AlertDialogTitle>
            <AlertDialogDescription>
              Você fez alterações nesta zona segura. Por favor, confirme se
              todos os dados estão corretos antes de salvar.
              <br />
              <br /> Lembre-se que sempre que você fizer mudanças no círculo no
              mapa, deve pressionar o botão salvar que fica ao lado das
              ferramentas de desenho para confirmar as alterações na área.
              Verifique se o círculo está posicionado corretamente antes de
              continuar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center space-x-2 py-4 px-6">
            <Checkbox
              id="dontShowAgain"
              checked={dontShowAgain}
              onCheckedChange={(checked) =>
                setDontShowAgain(checked as boolean)
              }
            />
            <label
              htmlFor="dontShowAgain"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Não exibir mais este aviso
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Revisar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave}>
              Confirmar e Salvar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SafeZoneForm;
